import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import ConfirmHost from "@/components/ConfirmHost";
import PdfLoadingOverlay from "@/components/PdfLoadingOverlay";
const ReinstateApply = lazy(() => import("@/pages/ReinstateApply"));

import { AuthProvider, useAuth } from "@/context/AuthContext";
import IdleTimeout from "@/components/IdleTimeout";
import SessionExpiryBanner from "@/components/SessionExpiryBanner";
import ChatWidget from "@/components/ChatWidget";
import PushRegistrar from "@/components/PushRegistrar";
import { installDateInputHints } from "@/lib/dateInputHints";
installDateInputHints();

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Dispatch = lazy(() => import("@/pages/Dispatch"));
const Brokerage = lazy(() => import("@/pages/Brokerage"));
const Careers = lazy(() => import("@/pages/Careers"));
const News = lazy(() => import("@/pages/News"));
const Review = lazy(() => import("@/pages/Review"));
const Contact = lazy(() => import("@/pages/Contact"));
const RequestQuote = lazy(() => import("@/pages/RequestQuote"));
const CarrierPortal = lazy(() => import("@/pages/CarrierPortal"));
import Login from "@/pages/Login";
const Signup = lazy(() => import("@/pages/Signup"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const DemoInvite = lazy(() => import("@/pages/DemoInvite"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const PaymentSuccess = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/Payment").then((m) => ({ default: m.PaymentCancel })));

import PortalLayout from "@/components/portal/PortalLayout";
const PortalDashboard = lazy(() => import("@/pages/portal/Dashboard"));
const CarrierApps = lazy(() => import("@/pages/portal/CarrierApps"));
const CarrierReviewQueue = lazy(() => import("@/pages/portal/CarrierReviewQueue"));
const FellOffScorecard = lazy(() => import("@/pages/portal/FellOffScorecard"));
const Quotes = lazy(() => import("@/pages/portal/Quotes"));
const SOPPortal = lazy(() => import("@/pages/portal/SOPPortal"));
const Team = lazy(() => import("@/pages/portal/Team"));
const CertificateWall = lazy(() => import("@/pages/portal/CertificateWall"));
const LeadFinder = lazy(() => import("@/pages/portal/LeadFinder"));
const CarrierCalls = lazy(() => import("@/pages/portal/CarrierCalls"));
const LoadLists = lazy(() => import("@/pages/portal/LoadLists"));
const ShipperDemo = lazy(() => import("@/pages/ShipperDemo"));
const Promoters = lazy(() => import("@/pages/portal/Promoters"));
const CarrierCheck = lazy(() => import("@/pages/portal/CarrierCheck"));
const ClosedLoadsArchive = lazy(() => import("@/pages/portal/ClosedLoadsArchive"));
const Settings = lazy(() => import("@/pages/portal/Settings"));
const DashboardSettings = lazy(() => import("@/pages/DashboardSettings"));
const PromoterPortal = lazy(() => import("@/pages/PromoterPortal"));
const ReferidoForm = lazy(() => import("@/pages/ReferidoForm"));
const CarrierDocRequest = lazy(() => import("@/pages/CarrierDocRequest"));
const Organization = lazy(() => import("@/pages/portal/Organization"));
const Loads = lazy(() => import("@/pages/portal/Loads"));
const LoadDetail = lazy(() => import("@/pages/portal/LoadDetail"));
const Training = lazy(() => import("@/pages/portal/Training"));
const TeamPipeline = lazy(() => import("@/pages/portal/TeamPipeline"));
const CarrierHq = lazy(() => import("@/pages/portal/CarrierHq"));
const HqLanding = lazy(() => import("@/pages/hq/HqLanding"));
const HqLayout = lazy(() => import("@/pages/hq/HqLayout"));
const HqDashboard = lazy(() => import("@/pages/hq/HqDashboard"));
const HqLoads = lazy(() => import("@/pages/hq/HqLoads"));
const HqFleet = lazy(() => import("@/pages/hq/HqFleet"));
const HqTeam = lazy(() => import("@/pages/hq/HqTeam"));
const HqAccounting = lazy(() => import("@/pages/hq/HqAccounting"));
const HqGoLive = lazy(() => import("@/pages/hq/HqGoLive"));
const HqAnalyticsPrint = lazy(() => import("@/pages/hq/HqAnalyticsPrint"));
const HqBilling = lazy(() => import("@/pages/hq/HqBilling"));
const HqCompany = lazy(() => import("@/pages/hq/HqCompany"));
const HqHeatmap = lazy(() => import("@/pages/hq/HqHeatmap"));
const HqDriver = lazy(() => import("@/pages/hq/HqDriver"));
const HqLoginPage = lazy(() => import("@/pages/hq/HqLogin").then((m) => ({ default: m.HqLogin })));
const HqInvitePage = lazy(() => import("@/pages/hq/HqLogin").then((m) => ({ default: m.HqInvite })));
const HqForgotPage = lazy(() => import("@/pages/hq/HqLogin").then((m) => ({ default: m.HqForgot })));
const HqResetPage = lazy(() => import("@/pages/hq/HqLogin").then((m) => ({ default: m.HqReset })));
const HqDemoStartPage = lazy(() => import("@/pages/hq/HqLogin").then((m) => ({ default: m.HqDemoStart })));
const HqInbox = lazy(() => import("@/pages/hq/HqInbox"));
const HqDocuments = lazy(() => import("@/pages/hq/HqDocuments"));
const HqLoadBoard = lazy(() => import("@/pages/hq/HqLoadBoard"));
const HqBrokers = lazy(() => import("@/pages/hq/HqBrokers"));
const HqMap = lazy(() => import("@/pages/hq/HqMap"));
import { HqAuthProvider } from "@/hq/HqAuth";
import { CargaProvider } from "@/carga/CargaAuth";
const Invoices = lazy(() => import("@/pages/portal/Invoices"));
const Promotions = lazy(() => import("@/pages/portal/Promotions"));
const DispatchBoard = lazy(() => import("@/pages/portal/DispatchBoard"));
const DriverMessages = lazy(() => import("@/pages/portal/DriverMessages"));
const BrokerEmails = lazy(() => import("@/pages/portal/BrokerEmails"));
const DriverAppInstallCard = lazy(() => import("@/pages/portal/DriverAppInstallCard"));
const HomeTimeCalendar = lazy(() => import("@/pages/portal/HomeTimeCalendar"));
const CarrierDetail = lazy(() => import("@/pages/portal/CarrierDetail"));
const CarrierRankings = lazy(() => import("@/pages/portal/CarrierRankings"));
const CarriersList = lazy(() => import("@/pages/portal/CarriersList"));
const BrokerageCarriers = lazy(() => import("@/pages/portal/BrokerageCarriers"));
const SignedAgreements = lazy(() => import("@/pages/portal/SignedAgreements"));
const Compliance = lazy(() => import("@/pages/portal/Compliance"));
const DataHygiene = lazy(() => import("@/pages/portal/DataHygiene"));
const Integrations = lazy(() => import("@/pages/portal/Integrations"));
const Shippers = lazy(() => import("@/pages/portal/Shippers"));
const Brokers = lazy(() => import("@/pages/portal/Brokers"));
const Crm = lazy(() => import("@/pages/portal/Crm"));
const BrokerageLoads = lazy(() => import("@/pages/portal/BrokerageLoads"));
const DriverConsents = lazy(() => import("@/pages/portal/DriverConsents"));
const AdScheduler = lazy(() => import("@/pages/portal/AdScheduler"));
const DispatcherApplications = lazy(() => import("@/pages/portal/DispatcherApplications"));
const IndepEarnings = lazy(() => import("@/pages/portal/IndepEarnings"));
const IndependentDispatchers = lazy(() => import("@/pages/IndependentDispatchers"));
const DispatcherSignup = lazy(() => import("@/pages/DispatcherSignup"));
const BrandKit = lazy(() => import("@/pages/BrandKit"));
import { UpdateBanner } from "@/components/UpdateBanner";
const Accounting = lazy(() => import("@/pages/portal/Accounting"));
const EmployeePay = lazy(() => import("@/pages/portal/EmployeePay"));
const MyPayroll = lazy(() => import("@/pages/portal/MyPayroll"));
const CommissionLedger = lazy(() => import("@/pages/portal/CommissionLedger"));
const AgentQuotes = lazy(() => import("@/pages/portal/AgentQuotes"));
const DnuRequests = lazy(() => import("@/pages/portal/DnuRequests"));
const Claims = lazy(() => import("@/pages/portal/Claims"));
const MarketHeatmap = lazy(() => import("@/pages/portal/MarketHeatmap"));
const LoadMap = lazy(() => import("@/pages/portal/LoadMap"));
const Recruiting = lazy(() => import("@/pages/portal/Recruiting"));
const Acquisition = lazy(() => import("@/pages/portal/Acquisition"));
const CargaAdmin = lazy(() => import("@/pages/portal/CargaAdmin"));
const CargaLanding = lazy(() => import("@/pages/carga/CargaLanding"));
const CargaLayout = lazy(() => import("@/pages/carga/CargaLayout"));
const CargaBoard = lazy(() => import("@/pages/carga/CargaBoard"));
const CargaMyLoads = lazy(() => import("@/pages/carga/CargaMyLoads"));
const CargaMessages = lazy(() => import("@/pages/carga/CargaMessages"));
const CargaAgreements = lazy(() => import("@/pages/carga/CargaAgreements"));
const CargaHistory = lazy(() => import("@/pages/carga/CargaHistory"));
const CargaSupport = lazy(() => import("@/pages/carga/CargaSupport"));
const CargaAccount = lazy(() => import("@/pages/carga/CargaAccount"));
const CargaLoginPage = lazy(() => import("@/pages/carga/CargaAuthPages").then((m) => ({ default: m.CargaLogin })));
const CargaRegisterPage = lazy(() => import("@/pages/carga/CargaAuthPages").then((m) => ({ default: m.CargaRegister })));
const CargaIndex = lazy(() => import("@/pages/carga/CargaIndex"));
const CargaInviteAccept = lazy(() => import("@/pages/carga/CargaInviteAccept"));
const RateTools = lazy(() => import("@/pages/portal/RateTools"));
const LeadershipAI = lazy(() => import("@/pages/portal/LeadershipAI"));
const GrowthHub = lazy(() => import("@/pages/portal/GrowthHub"));
const GrowthScript = lazy(() => import("@/pages/portal/GrowthScript"));
const SmsAlerts = lazy(() => import("@/pages/portal/SmsAlerts"));
const CommandCenter = lazy(() => import("@/pages/portal/CommandCenter"));
const DocumentControl = lazy(() => import("@/pages/portal/DocumentControl"));
const AccountingQueue = lazy(() => import("@/pages/portal/AccountingQueue"));
const DispatcherAgreements = lazy(() => import("@/pages/portal/DispatcherAgreements"));
const ActivateCarrier = lazy(() => import("@/pages/ActivateCarrier"));
const SmsOptIn = lazy(() => import("@/pages/SmsOptIn"));
const SharedDocs = lazy(() => import("@/pages/SharedDocs"));

function PortalIndex() {
  const { user } = useAuth();
  // Land owner/admin on the Command Center once per session; after that the
  // Dashboard tab renders the normal dashboard. The flag is set by CommandCenter on mount.
  if (user && ["owner", "administrator"].includes(user.role) && !sessionStorage.getItem("mfg_cc_landed")) {
    return <Navigate to="/portal/command-center" replace />;
  }
  return <PortalDashboard />;
}
const AdminAnalytics = lazy(() => import("@/pages/portal/AdminAnalytics"));
const TakeHome = lazy(() => import("@/pages/portal/TakeHome"));
const EsignInbox = lazy(() => import("@/pages/portal/EsignInbox"));
const EsignSign = lazy(() => import("@/pages/EsignSign"));
const PublicLoadBoard = lazy(() => import("@/pages/PublicLoadBoard"));
const PublicTracking = lazy(() => import("@/pages/PublicTracking"));
const DriverTrack = lazy(() => import("@/pages/DriverTrack"));
const DriverAppOpen = lazy(() => import("@/pages/DriverAppOpen"));
const DriverAppDownload = lazy(() => import("@/pages/DriverAppDownload"));
const FuelStop = lazy(() => import("@/pages/FuelStop"));
const DriverHome = lazy(() => import("@/pages/DriverHome"));
const PublicQuote = lazy(() => import("@/pages/PublicQuote"));
const CoiUpload = lazy(() => import("@/pages/CoiUpload"));
const PublicRateConSign = lazy(() => import("@/pages/PublicRateConSign"));
const WhyMFG = lazy(() => import("@/pages/WhyMFG"));
const AdGallery = lazy(() => import("@/pages/AdGallery"));
const PitchPage = lazy(() => import("@/pages/PitchPage"));
import { PwaInstallProvider } from "@/context/PwaInstallContext";
import { LanguageProvider } from "@/i18n";
import OfflineQueueBootstrap from "@/components/OfflineQueueBootstrap";
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

// Detect Google OAuth return synchronously during render (before any route runs).
function GoogleAuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function RouteFallback() {
  return (
    <div className="min-h-[40vh] grid place-items-center" data-testid="route-loading">
      <div className="h-7 w-7 rounded-full border-2 border-neutral-300 border-t-[#0A84FF] animate-spin" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
      <PwaInstallProvider>
      <BrowserRouter>
        <ConfirmHost />
        <Toaster position="top-right" richColors closeButton duration={1000} mobileOffset={{ top: 72, left: 12, right: 12 }} toastOptions={{ className: "max-w-[calc(100vw-1.5rem)] break-words" }} />
        <PdfLoadingOverlay />
        <IdleTimeout />
        <SessionExpiryBanner />
        <UpdateBanner />
        {!window.location.pathname.startsWith("/hq") && <ChatWidget />}
        <PushRegistrar />
        <OfflineQueueBootstrap />
        <GoogleAuthGate>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/carga" element={<CargaProvider><CargaLanding /></CargaProvider>} />
          <Route path="/carga/login" element={<CargaProvider><CargaLoginPage /></CargaProvider>} />
          <Route path="/carga/registro" element={<CargaProvider><CargaRegisterPage /></CargaProvider>} />
          <Route path="/carga/invitacion/:token" element={<CargaProvider><CargaInviteAccept /></CargaProvider>} />
          <Route path="/carga/app" element={<CargaProvider><CargaLayout /></CargaProvider>}>
            <Route index element={<CargaIndex />} />
            <Route path="tablero" element={<CargaBoard />} />
            <Route path="cargas" element={<CargaMyLoads />} />
            <Route path="mensajes" element={<CargaMessages />} />
            <Route path="acuerdos" element={<CargaAgreements />} />
            <Route path="historial" element={<CargaHistory />} />
            <Route path="soporte" element={<CargaSupport />} />
            <Route path="cuenta" element={<CargaAccount />} />
          </Route>
          <Route path="/hq" element={<HqAuthProvider><HqLanding /></HqAuthProvider>} />
          <Route path="/hq/login" element={<HqAuthProvider><HqLoginPage /></HqAuthProvider>} />
          <Route path="/hq/invite/:token" element={<HqAuthProvider><HqInvitePage /></HqAuthProvider>} />
          <Route path="/hq/forgot" element={<HqAuthProvider><HqForgotPage /></HqAuthProvider>} />
          <Route path="/hq/reset/:token" element={<HqAuthProvider><HqResetPage /></HqAuthProvider>} />
          <Route path="/hq/demo/:token" element={<HqAuthProvider><HqDemoStartPage /></HqAuthProvider>} />
          <Route path="/hq/d/:token" element={<HqDriver />} />
          <Route path="/hq/app/analytics/print" element={<HqAuthProvider><HqAnalyticsPrint /></HqAuthProvider>} />
          <Route path="/hq/app" element={<HqAuthProvider><HqLayout /></HqAuthProvider>}>
            <Route index element={<HqDashboard />} />
            <Route path="loads" element={<HqLoads />} />
            <Route path="inbox" element={<HqInbox />} />
            <Route path="documents" element={<HqDocuments />} />
            <Route path="loadboard" element={<HqLoadBoard />} />
            <Route path="fleet" element={<HqFleet />} />
            <Route path="brokers" element={<HqBrokers />} />
            <Route path="map" element={<HqMap />} />
            <Route path="team" element={<HqTeam />} />
            <Route path="accounting" element={<HqAccounting />} />
            <Route path="go-live" element={<HqGoLive />} />
            <Route path="market" element={<HqHeatmap />} />
            <Route path="billing" element={<HqBilling />} />
            <Route path="company" element={<HqCompany />} />
          </Route>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/why-mfg" element={<WhyMFG />} />
          <Route path="/independent-dispatchers" element={<IndependentDispatchers />} />
          <Route path="/dispatcher-signup" element={<DispatcherSignup />} />
          <Route path="/brand-kit" element={<BrandKit />} />
          <Route path="/ad-library" element={<AdGallery />} />
          <Route path="/loads" element={<PublicLoadBoard />} />
          <Route path="/track/:loadId" element={<PublicTracking />} />
          <Route path="/drive" element={<DriverHome />} />
          <Route path="/drive/:loadId" element={<DriverTrack />} />
          <Route path="/p/:code" element={<PitchPage />} />
          <Route path="/go/drive/:loadId" element={<DriverAppOpen />} />
          <Route path="/driver-app.apk" element={<DriverAppDownload autoDownload />} />
          <Route path="/driver-app" element={<DriverAppDownload />} />
          <Route path="/driver-app/print" element={<DriverAppInstallCard />} />
          <Route path="/fuel/:loadId/:token" element={<FuelStop />} />
          <Route path="/quote/:quoteId" element={<PublicQuote />} />
          <Route path="/coi-upload/:carrierId/:token" element={<CoiUpload />} />
          <Route path="/rate-con/:loadId" element={<PublicRateConSign />} />
          <Route path="/sign/:token" element={<EsignSign />} />
          <Route path="/share/docs/:token" element={<SharedDocs />} />
          <Route path="/dispatch" element={<Dispatch />} />
          <Route path="/brokerage" element={<Brokerage />} />
          <Route path="/carrier" element={<CarrierPortal />} />
          <Route path="/activate" element={<ActivateCarrier />} />
          <Route path="/sms-opt-in" element={<SmsOptIn />} />
          <Route path="/quote" element={<RequestQuote />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/news" element={<News />} />
          <Route path="/review" element={<Review />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/demo-invite/:token" element={<DemoInvite />} />
          <Route path="/shipper-demo/:token" element={<ShipperDemo />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/settings" element={<DashboardSettings />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/promoter" element={<PromoterPortal />} />
          <Route path="/referido" element={<ReferidoForm />} />
          <Route path="/carrier-docs/:token" element={<CarrierDocRequest />} />
          <Route path="/reinstate/:cid" element={<ReinstateApply />} />

          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalIndex />} />
            <Route path="command-center" element={<CommandCenter />} />
            <Route path="documents" element={<DocumentControl />} />
            <Route path="accounting-queue" element={<AccountingQueue />} />
            <Route path="dispatcher-agreements" element={<DispatcherAgreements />} />
            <Route path="loads" element={<Loads />} />
            <Route path="loads/:loadId" element={<LoadDetail />} />
            <Route path="training" element={<Training />} />
            <Route path="team-pipeline" element={<TeamPipeline />} />
            <Route path="carrier-hq" element={<CarrierHq />} />
            <Route path="carriers" element={<CarrierApps />} />
            <Route path="carrier-review" element={<CarrierReviewQueue />} />
            <Route path="fell-off-scorecard" element={<FellOffScorecard />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="invoices" element={<Invoices kind="carrier" />} />
            <Route path="customer-invoices" element={<Invoices kind="customer" />} />
            <Route path="promotions" element={<Promotions />} />
            <Route path="promoters" element={<Promoters />} />
            <Route path="carrier-check" element={<CarrierCheck />} />
            <Route path="archive" element={<ClosedLoadsArchive />} />
            <Route path="settings" element={<Settings />} />
            <Route path="dispatch" element={<DispatchBoard />} />
            <Route path="driver-messages" element={<DriverMessages />} />
            <Route path="broker-emails" element={<BrokerEmails />} />
            <Route path="driver-app" element={<DriverAppInstallCard />} />
            <Route path="home-time" element={<HomeTimeCalendar />} />
            <Route path="my-carriers" element={<CarriersList />} />
            <Route path="brokerage-carriers" element={<BrokerageCarriers />} />
            <Route path="carriers/:id" element={<CarrierDetail />} />
            <Route path="carrier-rankings" element={<CarrierRankings />} />
            <Route path="sop" element={<SOPPortal />} />
            <Route path="agreements" element={<SignedAgreements />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="data-hygiene" element={<DataHygiene />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="shippers" element={<Shippers />} />
            <Route path="brokers" element={<Brokers />} />
            <Route path="crm" element={<Crm />} />
            <Route path="brokerage-loads" element={<BrokerageLoads />} />
            <Route path="driver-consents" element={<DriverConsents />} />
            <Route path="ad-scheduler" element={<AdScheduler />} />
            <Route path="dispatcher-applications" element={<DispatcherApplications />} />
            <Route path="independent-earnings" element={<IndepEarnings />} />
            <Route path="accounting" element={<Accounting />} />
            <Route path="employee-pay" element={<EmployeePay />} />
            <Route path="my-payroll" element={<MyPayroll />} />
            <Route path="commission-ledger" element={<CommissionLedger />} />
            <Route path="agent-quotes" element={<AgentQuotes />} />
            <Route path="dnu-requests" element={<DnuRequests />} />
            <Route path="claims" element={<Claims />} />
            <Route path="heatmap" element={<MarketHeatmap />} />
            <Route path="load-map" element={<LoadMap />} />
            <Route path="recruiting" element={<Recruiting />} />
            <Route path="acquisition" element={<Acquisition />} />
            <Route path="carga" element={<CargaAdmin />} />
            <Route path="rate-tools" element={<RateTools />} />
            <Route path="assistant" element={<Navigate to="/portal/leadership-ai" replace />} />
            <Route path="leadership-ai" element={<LeadershipAI />} />
            <Route path="growth" element={<GrowthHub />} />
            <Route path="growth/script/:id" element={<GrowthScript />} />
            <Route path="sms-alerts" element={<SmsAlerts />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="take-home" element={<TakeHome />} />
            <Route path="esign" element={<EsignInbox />} />
            <Route path="esign/:envelopeId" element={<EsignInbox />} />
            <Route path="team" element={<Team />} />
            <Route path="certificate-wall" element={<CertificateWall />} />
            <Route path="lead-finder" element={<LeadFinder />} />
            <Route path="carrier-calls" element={<CarrierCalls />} />
            <Route path="load-lists" element={<LoadLists />} />
            <Route path="organization" element={<Organization />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Route>
        </Routes>
        </Suspense>
        </GoogleAuthGate>
      </BrowserRouter>
      </PwaInstallProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
